import { useState } from 'react';
import type { ColunasAntigasItem, ConverterExcecaoResponse } from '@triade/contracts';
import { useColunasAntigas, useConverterExcecao } from '@/api/itens-tributos-colunas-antigas';
import { ErroLista } from '@/components/cargos';
import { formatarContagem, formatarData, formatarTaxa } from '@/lib/format';
import { mensagemErroTabela } from './tabela-form';

const BOTAO_PRIMARIO = 'h-[34px] rounded-[10px] bg-primary px-4 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50';
const CHIP_COLUNA = 'tabular';

/**
 * Sessao 51b (U8): itens com valores nas colunas antigas (PIS/COFINS/ICMS/ISS) que a tabela estruturada nao usa. Seleciona
 * itens e converte em excecao em lote. ICMS so entra com a caixa marcada (o ICMS do item valeria em todas as UFs).
 * Montado so com tributo.editar e custo.ver (quem chama decide). Nada e calculado aqui: os valores vem da API.
 */
export function ColunasAntigasItens() {
  const colunas = useColunasAntigas(true);
  const converter = useConverterExcecao();
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [incluirIcms, setIncluirIcms] = useState(false);
  const [resultado, setResultado] = useState<ConverterExcecaoResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (colunas.isError && !colunas.data) {
    return <ErroLista erro={colunas.error} onTentar={() => void colunas.refetch()} titulo="Não foi possível carregar os itens com colunas antigas" />;
  }
  if (!colunas.data) return <p role="status" className="text-small text-ink-muted">Carregando itens com colunas antigas…</p>;
  if (!colunas.data.tabelaEstruturada) return null;

  const itens: ColunasAntigasItem[] = colunas.data.itens;
  const todos = itens.length > 0 && itens.every((i) => marcados.has(i.itemId));

  function alternar(itemId: string) {
    const proximo = new Set(marcados);
    if (proximo.has(itemId)) proximo.delete(itemId);
    else proximo.add(itemId);
    setMarcados(proximo);
  }

  async function converterSelecionados() {
    setErro(null);
    setResultado(null);
    // Padrao: pis, cofins e iss. ICMS so com a caixa marcada explicitamente.
    const campos = incluirIcms ? (['pis', 'cofins', 'icms', 'iss'] as const) : (['pis', 'cofins', 'iss'] as const);
    try {
      const r = await converter.mutateAsync({ itemIds: [...marcados], campos: [...campos] });
      setResultado(r);
      setMarcados(new Set());
      setIncluirIcms(false);
    } catch (e) {
      setErro(mensagemErroTabela(e));
    }
  }

  return (
    <section aria-label="Itens com colunas antigas" className="mt-3 border-t border-line-soft pt-3">
      <h4 className="text-small font-semibold">Itens com colunas antigas</h4>
      <p className="mt-1 text-caption text-ink-secondary">
        PIS, COFINS, ICMS e ISS nas colunas antigas do item não entram no cálculo da tabela estruturada. Converter em exceção grava o valor como exceção do item, que passa a valer no cálculo.
      </p>

      {itens.length === 0 ? (
        <p role="status" className="mt-2 text-small text-ink-muted">Nenhum item com valores nas colunas antigas.</p>
      ) : (
        <>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-small" aria-label="Itens com colunas antigas">
              <thead>
                <tr className="text-left text-caption text-ink-muted">
                  <th scope="col" className="py-1.5 pr-2 font-medium">
                    <input
                      type="checkbox"
                      aria-label="Selecionar todos os itens"
                      checked={todos}
                      onChange={(e) => setMarcados(e.target.checked ? new Set(itens.map((i) => i.itemId)) : new Set())}
                    />
                  </th>
                  <th scope="col" className="py-1.5 pr-2 font-medium">Código</th>
                  <th scope="col" className="py-1.5 pr-2 font-medium">Descrição</th>
                  <th scope="col" className="py-1.5 pr-2 text-right font-medium">PIS</th>
                  <th scope="col" className="py-1.5 pr-2 text-right font-medium">COFINS</th>
                  <th scope="col" className="py-1.5 pr-2 text-right font-medium">ICMS</th>
                  <th scope="col" className="py-1.5 pr-2 text-right font-medium">ISS</th>
                  <th scope="col" className="py-1.5 font-medium">Desde</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((i) => (
                  <tr key={i.itemId} className="border-t border-line-soft">
                    <td className="py-1.5 pr-2">
                      <input type="checkbox" aria-label={`Selecionar ${i.codigo}`} checked={marcados.has(i.itemId)} onChange={() => alternar(i.itemId)} />
                    </td>
                    <td className="py-1.5 pr-2 font-semibold">{i.codigo}</td>
                    <td className="py-1.5 pr-2">{i.descricao}</td>
                    <td className={`${CHIP_COLUNA} py-1.5 pr-2 text-right`}>{formatarTaxa(i.pis)}</td>
                    <td className={`${CHIP_COLUNA} py-1.5 pr-2 text-right`}>{formatarTaxa(i.cofins)}</td>
                    <td className={`${CHIP_COLUNA} py-1.5 pr-2 text-right`}>{formatarTaxa(i.icms)}</td>
                    <td className={`${CHIP_COLUNA} py-1.5 pr-2 text-right`}>{formatarTaxa(i.iss)}</td>
                    <td className="tabular py-1.5">{formatarData(i.validFrom)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-small text-ink-body">
              <input type="checkbox" checked={incluirIcms} onChange={(e) => setIncluirIcms(e.target.checked)} />
              Converter também o ICMS
            </label>
            <span className="text-caption text-ink-secondary">O ICMS do item valeria em todas as UFs.</span>
            <button type="button" className={BOTAO_PRIMARIO} disabled={marcados.size === 0 || converter.isPending} onClick={() => void converterSelecionados()}>
              Converter em exceção
            </button>
            <span className="text-caption text-ink-muted">{`${formatarContagem(marcados.size)} selecionado(s)`}</span>
          </div>
        </>
      )}

      {erro && <p role="alert" className="mt-2 text-small text-danger-ink">{erro}</p>}
      {resultado && (
        <p role="status" className="mt-2 text-small text-success-ink">
          {`Convertidos: ${formatarContagem(resultado.convertidos)}. Ignorados: ${formatarContagem(resultado.ignorados)}.`}
        </p>
      )}
    </section>
  );
}
